import { useEffect, useRef, useState } from 'react'
// W-n / E-n in comments are the review-warning ids of the committed plan,
// docs/superpowers/plans/2026-09-19-cubbo-2818-event-close-reconciliation-wms.md.
import { useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Tabs } from 'antd'
import { SlidePanel, ButtonLoader, Loader } from '../../../components'
import { fetchCoverageEvent, closeCoverageEvent, cancelCoverageEvent } from '../../../services/coverageEventServices'
import { coverageEventQueryKeys } from './coverageEventQueryKeys'
import { mapCoverageEventError } from './coverageEventErrors'
import { eventLabel } from './eventLabel'
import { CoverageEventDifferences } from './CoverageEventDifferences'
import { CoverageEventDrift } from './CoverageEventDrift'
import { CoverageEventReconciliation } from './CoverageEventReconciliation'
import { TablePaginator } from './TablePaginator'
import { Pill } from './Pill'
import { LIST_REFRESH_STATES, canCancel, footerAction, mergeEventHeader, showsReconciliationTab } from './reconciliationState'

const T = 'cycle_counts.events.detail'
const EVENTS_T = 'cycle_counts.events'
const { TabPane } = Tabs
const TOTAL_TILES = ['locations', 'satisfied', 'in_review', 'assigned', 'unassigned', 'uncovered']
const STATE_COLORS = {
  satisfied: 'bg-green-100 text-green-800',
  in_review: 'bg-blue-100 text-blue-800',
  assigned: 'bg-indigo-100 text-indigo-800',
  unassigned: 'bg-gray-100 text-gray-800',
  uncovered: 'bg-red-100 text-red-800',
}
const PER_PAGE = 100

export const CoverageEventDetailPanel = ({ eventId, warehouseId, onClose, onChanged, onGateOff }) => {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('locations')
  const [stateFilter, setStateFilter] = useState('')
  const [page, setPage] = useState(1)
  // `pending` names the footer mutation of this panel in flight ('cancel' | 'reconcile' | 'close');
  // `tabBusy` is the Conciliación tab's. Every control disables on either, but a button only says
  // "loading" for its own mutation — Cancelar sits next to Conciliar for the same event.
  const [pending, setPending] = useState(null)
  const [tabBusy, setTabBusy] = useState(false)
  const busy = pending != null || tabBusy
  const resetBusy = () => {
    setPending(null)
    setTabBusy(false)
  }
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  // Local to this panel: a drift-add attempt can surface COVERAGE_EVENTS_DISABLED before the
  // Eventos tab's own banner has (e.g. the tab was opened before anyone tried to write); it
  // still forwards to onGateOff so the tab-level banner and Create button stay in sync.
  const [driftGateOff, setDriftGateOff] = useState(false)

  useEffect(() => {
    setPage(1)
  }, [stateFilter, eventId])

  // Tracked from onError/onSuccess rather than react-query's derived `isError`: once a
  // background refetch fails and then the user retries, react-query's own `keepPreviousData`
  // optimistically reports the retry attempt as a (borrowed, stale) success — flipping the
  // derived `isError` back to false mid-retry — which would otherwise make the error banner
  // (and its Retry button) disappear the instant it's clicked. onError/onSuccess fire once per
  // settled fetch regardless of that placeholder-data bookkeeping, so this stays accurate.
  const [loadError, setLoadError] = useState(false)
  useEffect(() => {
    setLoadError(false)
  }, [eventId])

  // W-5: after a successful execute the Conciliación tab hands back the header the platform
  // returned; rendering it at once keeps the footer and the state badge from lagging a whole
  // round-trip behind the action just taken. The detail query's own payload is always the
  // fresher of the two, so any successful fetch retires the override.
  const [headerOverride, setHeaderOverride] = useState(null)
  // "Did the execute happen?" is held here, keyed by event, not in the pane: the pane remounts
  // on a tab switch and the panel is closed and reopened, and neither may bring Aprobar (or
  // Cancelar) back before a real read-back answered. Only that answer clears it.
  const [eventsWithUnknownOutcome, setEventsWithUnknownOutcome] = useState(() => new Set())
  const executeOutcomeUnknown = eventId != null && eventsWithUnknownOutcome.has(Number(eventId))
  const setExecuteOutcomeUnknown = value =>
    setEventsWithUnknownOutcome(previous => {
      const next = new Set(previous)
      if (value) next.add(Number(eventId))
      else next.delete(Number(eventId))
      return next
    })
  useEffect(() => {
    setHeaderOverride(null)
    setError(null)
    setNotice(null)
    // A mutation started for the previous event must not keep this one's footer and tabs frozen;
    // its own continuation is ignored by `stillCurrent` anyway.
    resetBusy()
  }, [eventId])
  // The panel outlives the event it shows: close it mid-mutation and open another, and the late
  // response would land on the new event (its header on screen, the footer's Cancelar aimed at
  // the other id). Every async continuation checks it still belongs to the event on screen.
  const currentEventId = useRef(eventId)
  currentEventId.current = eventId
  const stillCurrent = id => currentEventId.current === id

  const params = { state: stateFilter || undefined, page, per_page: PER_PAGE }
  const { data, isLoading, isFetching, isPreviousData, refetch } = useQuery(
    coverageEventQueryKeys.detail(eventId, params),
    () => fetchCoverageEvent(eventId, params),
    {
      enabled: eventId != null,
      refetchOnWindowFocus: true,
      refetchOnMount: 'always',
      keepPreviousData: true,
      onError: () => setLoadError(true),
      onSuccess: () => {
        setLoadError(false)
        setHeaderOverride(null)
      },
    },
  )

  // `keepPreviousData` hands back the PRIOR query key's payload as `data` (with
  // `isPreviousData: true`) while a filtered/paged/event-switch fetch is still pending — that's
  // desirable within the same event (avoids a flash to the loader on a filter/page change), but
  // not across an eventId switch, where it would render event A's rows under event B's header.
  // `lastGoodData` tracks the last payload that actually resolved for the CURRENT event, is
  // cleared on eventId change, and is what we show whenever `data` is only a placeholder.
  const [lastGoodData, setLastGoodData] = useState(null)
  useEffect(() => {
    setLastGoodData(null)
  }, [eventId])
  useEffect(() => {
    if (data && !isPreviousData) setLastGoodData(data)
  }, [data, isPreviousData])

  const shownData = isPreviousData ? lastGoodData : (data ?? lastGoodData)
  const event = mergeEventHeader(shownData?.event, headerOverride)
  const totals = shownData?.totals ?? {}
  const rows = shownData?.locations ?? []
  const meta = shownData?.meta ?? { total_pages: 0, current_page: 1 }

  const flagGateOff = () => {
    setDriftGateOff(true)
    onGateOff?.()
  }

  // `successKey`/`confirmKey` are copy keys under `ns` — the reconciliation actions live under
  // `cycle_counts.events.reconciliation`, everything else under the panel's own namespace.
  // `action` names the mutation for the error mapper ('close' on Conciliar); it is passed
  // explicitly rather than inferred from `fn`, so wrapping the service call cannot silently
  // degrade the 401 copy. `noticeParams(result)` interpolates the success notice.
  // Returns the mutation's payload on success and `null` on a refusal or a declined confirmation,
  // so a caller can both branch on the outcome and read the header the platform just returned.
  // Each run gets its own token: only the latest mutation of the event on screen may write its
  // outcome or release the lock (close mid-mutation, reopen the same event, start another).
  const runSeq = useRef(0)
  const run = async (fn, { key, successKey, confirmKey, noticeParams = () => ({}), ns = T, action }) => {
    if (!window.confirm(i18n.t(`${ns}.${confirmKey}`))) return null
    const id = eventId
    const seq = (runSeq.current += 1)
    const owns = () => stillCurrent(id) && runSeq.current === seq
    setPending(key)
    setError(null)
    setNotice(null)
    try {
      const result = await fn(id)
      // close/cancel return the event header + totals only; the location rows are refreshed by
      // invalidating every paged/filtered detail() variant instead of being spliced in here.
      // Cancel first: a detail fetch that predates the mutation carries the pre-mutation header,
      // and if it settles last it wipes the `headerOverride` the caller applies once this
      // resolves (`runReconcile`). `cancelRefetch` alone only cancels a query that already has
      // data — a first fetch of a new page or filter is not one — so the explicit cancel covers
      // both. Three arguments on purpose: `parseFilterArgs` reads arg 2 as FILTERS when arg 1 is a key.
      await queryClient.cancelQueries(coverageEventQueryKeys.detailAny(id))
      queryClient.invalidateQueries(coverageEventQueryKeys.detailAny(id), {}, { cancelRefetch: true })
      onChanged?.()
      if (!owns()) return null
      setNotice(i18n.t(`${ns}.${successKey}`, noticeParams(result)))
      // A 204 answers with '' — still a success, never a refusal.
      return result || {}
    } catch (err) {
      // The WMS has no notion of a settler, so a 401 on Conciliar names the missing authority.
      // A legacy "Cerrar evento" settles nothing, so its caller passes no action and its 401
      // keeps the plain FORBIDDEN copy.
      const mapped = mapCoverageEventError(err, i18n, { action })
      if (mapped.gateOff) flagGateOff()
      if (mapped.refetch) queryClient.invalidateQueries(coverageEventQueryKeys.detailAny(id), {}, { cancelRefetch: true })
      if (owns()) setError(mapped.message)
      return null
    } finally {
      // Only the latest run of the event on screen releases the lock: the panel may have moved on
      // to another event, or this event may have been closed and reopened with a newer run.
      if (owns()) setPending(null)
    }
  }

  // Conciliar and Reintentar el cálculo are the same idempotent PUT /close (W-1: the platform
  // re-enqueues the worker for an event already computing); only the copy differs, and either
  // way the operator belongs on the Conciliación tab once it lands.
  // A retry confirms with its own copy but lands on the same `reconciling` notice as a first
  // Conciliar (`retry_reconcile` is the button label, not a notice).
  const runReconcile = async (confirmKey = 'confirm_reconcile') => {
    const result = await run(closeCoverageEvent, { key: 'reconcile', successKey: 'reconciling', confirmKey, ns: `${EVENTS_T}.reconciliation`, action: 'close' })
    if (!result) return
    // The returned header already says `reconciling`, so applying it here (W-5) makes the pane
    // exist in the same render as the switch to it — otherwise the fallback below would bounce
    // the operator straight back to Ubicaciones while the detail refetch is still in flight.
    if (result.event) setHeaderOverride(result.event)
    setTab('reconciliation')
  }

  // The tab can disappear under the operator: cancelling from inside it (or a cancellation
  // landing from elsewhere) drops the pane while antd's activeKey still points at it, which
  // renders an empty body. Fall back to the first tab whenever the gate closes.
  const showsReconciliation = showsReconciliationTab(event)
  // The pane stays mounted while its own mutation is in flight: unmounting it then (a refetch
  // landing `canceled` while an approve is pending) would drop the refusal it is about to render.
  const keepsReconciliationPane = showsReconciliation || (tabBusy && tab === 'reconciliation')
  // A refusal from the tab lives on the panel's own error line, outside the tabs: whatever lands
  // next (a cancellation, a refetch, a tab switch) cannot take it away. It clears with the next
  // mutation, an event switch or a close, like every other panel-level message.
  // A pane unmounted by an event switch may still report: only the event on screen takes it.
  const handleReconciliationRefused = message => {
    if (stillCurrent(eventId)) setError(message)
  }
  useEffect(() => {
    if (tab === 'reconciliation' && !keepsReconciliationPane) setTab('locations')
  }, [tab, keepsReconciliationPane])

  // The Conciliación tab reports once per observed state transition, and once more with the
  // header the platform returned from an execute.
  const handleReconciliationChanged = async ({ header, state } = {}) => {
    // The tab that reports may belong to an event the panel no longer shows (a pane unmounted
    // by a switch still runs its async continuation): compare against the CURRENT event, not
    // the id this closure captured, and ignore the report entirely when they differ.
    const id = currentEventId.current
    if (header && header.id != null && Number(header.id) !== Number(id)) return
    if (!stillCurrent(eventId)) return
    // A detail fetch already in flight predates the execute and carries the frozen header; a
    // first fetch of a new filter is not cancelled by `cancelRefetch`, so cancel explicitly
    // before the header goes on and a fresh read starts.
    await queryClient.cancelQueries(coverageEventQueryKeys.detailAny(id))
    if (!stillCurrent(id)) return
    // Only this panel's own header: the cycle-count pools cannot change while the execution
    // runs, and the tab owns the single poll (W-5).
    if (header) setHeaderOverride(header)
    // "Evento en conciliación…" from the Conciliar click describes the state just left; under
    // the frozen proposal it reads as if the calculation were still running.
    setNotice(null)
    refetch({ cancelRefetch: true })
    // W-5 is binding: this handler calls only its own refetch, and the Eventos list refreshes
    // on execute/cancel success only (the plan's Task 4 snippet said otherwise; the controller
    // ruled for W-5). The two exceptions are the execute hand-back, which carries a header, and
    // a transition landing on a terminal state — those are what the list renders. A
    // computing→frozen hop changes nothing out there, so it stays inside this panel.
    if (header || LIST_REFRESH_STATES.includes(state)) onChanged?.()
  }
  const footer = footerAction(event)
  // The platform refuses unauthorised reads and actions on the event's own warehouse; this only
  // keeps the screen honest: an event of another warehouse (a crafted `?event_id=`) reads as not
  // found under this warehouse's screen, with no title, tabs or actions of its own. An older
  // platform sends no `warehouse_id`: nothing to compare, the event renders as before.
  const otherWarehouse = event?.warehouse_id != null && String(event.warehouse_id) !== String(warehouseId)

  if (eventId == null) return null

  return (
    <SlidePanel
      title={event && !otherWarehouse ? `${i18n.t(`${T}.title`)} #${event.id} · ${eventLabel(event, i18n)}` : i18n.t(`${T}.title`)}
      referrer={`/warehouse/${warehouseId}/cycle-counts`}
      onClose={() => {
        setStateFilter('')
        setPage(1)
        setTab('locations')
        setError(null)
        setNotice(null)
        setHeaderOverride(null)
        resetBusy()
        onClose?.()
      }}
    >
      {loadError && !shownData ? (
        <div className="text-center py-8 space-y-3">
          <p role="alert" className="text-sm text-red-700">
            {i18n.t(`${EVENTS_T}.load_error`)}
          </p>
          <ButtonLoader type="secondary" onClick={() => refetch()} loading={isFetching} disabled={isFetching}>
            {i18n.t(`${T}.retry`)}
          </ButtonLoader>
        </div>
      ) : otherWarehouse ? (
        <p role="alert" className="text-center py-8 text-sm text-red-700">
          {i18n.t(`${EVENTS_T}.errors.NOT_FOUND`)}
        </p>
      ) : isLoading || !shownData ? (
        <Loader show />
      ) : (
        <div className="space-y-4">
          {loadError && (
            <div className="flex items-center justify-between gap-2 rounded bg-red-50 border border-red-200 px-3 py-2">
              {/* This banner overlays rows already loaded (from a different filter/page), unlike
                  the full-page state above which has nothing behind it — say so explicitly. */}
              <p role="alert" className="text-sm text-red-700">
                {i18n.t(`${T}.load_error_stale`)}
              </p>
              <ButtonLoader type="secondary" onClick={() => refetch()} loading={isFetching} disabled={isFetching}>
                {i18n.t(`${T}.retry`)}
              </ButtonLoader>
            </div>
          )}
          <div data-testid="coverage-event-totals" className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
            {TOTAL_TILES.map(key => (
              <div key={key} className="rounded bg-gray-50 p-2">
                <div className="text-lg font-semibold text-gray-900">{totals[key] ?? 0}</div>
                <div className="text-xs text-gray-500">{i18n.t(`${T}.totals.${key}`)}</div>
              </div>
            ))}
          </div>

          {/* Diferencias and Conciliación mount only while active; Cambios stays mounted once visited
              because it holds the operator's bay selection. Switching away while a mutation is in
              flight would drop the refusal a pane is about to render, so the other tabs are visibly
              disabled until it settles. */}
          <Tabs activeKey={tab} onChange={setTab}>
            <TabPane tab={i18n.t(`${T}.tabs.locations`)} key="locations" disabled={busy && tab !== 'locations'}>
              <select
                aria-label={i18n.t(`${T}.columns.state`)}
                className="border-gray-300 rounded-md text-sm mb-2"
                value={stateFilter}
                onChange={e => setStateFilter(e.target.value)}
              >
                <option value="">{i18n.t(`${T}.state_filter_all`)}</option>
                {['satisfied', 'in_review', 'assigned', 'unassigned', 'uncovered'].map(s => (
                  <option key={s} value={s}>
                    {i18n.t(`${T}.state.${s}`)}
                  </option>
                ))}
              </select>
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    {['location', 'state', 'count', 'assigned_to'].map(c => (
                      <th key={c} className="px-3 py-1 text-left text-xs text-gray-500">
                        {i18n.t(`${T}.columns.${c}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.warehouse_location_id}>
                      <td className="px-3 py-1.5 text-sm font-mono text-gray-900">
                        {row.location_code}
                        {row.added_after_snapshot && <span className="ml-2 text-xs text-indigo-700">{i18n.t(`${T}.added_after_snapshot`)}</span>}
                      </td>
                      <td className="px-3 py-1.5 text-sm">
                        <Pill className={STATE_COLORS[row.state] || STATE_COLORS.uncovered}>{i18n.t(`${T}.state.${row.state}`)}</Pill>
                      </td>
                      <td className="px-3 py-1.5 text-sm text-gray-500">
                        {row.satisfied_by ? (
                          i18n.t(`${T}.satisfied_by`, {
                            id: row.satisfied_by.cycle_count_id,
                            origin: i18n.t(`cycle_counts.conteos.origin_${row.satisfied_by.origin}`),
                          })
                        ) : row.active_count ? (
                          <>
                            {`#${row.active_count.cycle_count_id} · ${i18n.t(`cycle_counts.task_panel.status_${row.active_count.status}`)}`}
                            <Pill
                              className={`ml-2 ${row.active_count.coverage_event_id === event?.id ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'}`}
                            >
                              {i18n.t(`cycle_counts.events.differences.${row.active_count.coverage_event_id === event?.id ? 'owned' : 'watched'}`)}
                            </Pill>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-sm text-gray-500">
                        {row.active_count ? row.active_count.assigned_to?.full_name || i18n.t(`${T}.unassigned`) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePaginator page={page} meta={meta} onPage={setPage} disabled={busy} />
            </TabPane>
            <TabPane tab={i18n.t(`${T}.tabs.differences`)} key="differences" disabled={busy && tab !== 'differences'}>
              {tab === 'differences' && <CoverageEventDifferences eventId={eventId} warehouseId={warehouseId} />}
            </TabPane>
            <TabPane tab={i18n.t(`${T}.tabs.drift`)} key="drift" disabled={busy && tab !== 'drift'}>
              <CoverageEventDrift
                eventId={eventId}
                enabled={tab === 'drift' && event?.status === 'open'}
                gateOff={driftGateOff}
                onAdded={() => {
                  refetch()
                  onChanged?.()
                }}
                onGateOff={flagGateOff}
              />
            </TabPane>
            {keepsReconciliationPane && (
              <TabPane tab={i18n.t(`${EVENTS_T}.reconciliation.tab`)} key="reconciliation" disabled={busy && tab !== 'reconciliation'}>
                {/* Keyed by event so page, notices and the transition ref never carry over when
                    a deep link drives a new id into an already-open panel. */}
                {tab === 'reconciliation' && (
                  <CoverageEventReconciliation
                    key={eventId}
                    eventId={eventId}
                    warehouseId={warehouseId}
                    event={event}
                    busy={busy}
                    onChanged={handleReconciliationChanged}
                    onBusyChange={setTabBusy}
                    onGateOff={flagGateOff}
                    onRefused={handleReconciliationRefused}
                    outcomeUnknown={executeOutcomeUnknown}
                    onOutcomeUnknownChange={setExecuteOutcomeUnknown}
                  />
                )}
              </TabPane>
            )}
          </Tabs>

          {/* The hold hides Cancelar wherever the panel opens; the pane, with the retry, is only on
              the Conciliación tab — say so here, outside the tabs. */}
          {executeOutcomeUnknown && tab !== 'reconciliation' && (
            <p role="alert" className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              {i18n.t(`${EVENTS_T}.reconciliation.outcome_unknown_panel`)}
            </p>
          )}
          {error && (
            <p role="alert" className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}
          {notice && (
            <p role="status" className="text-sm text-green-700">
              {notice}
            </p>
          )}
          {/* Cancelar outlives `open` now: it stays until the execution is approved (W-1/W-5).
              An older platform, with no reconciliation_state, keeps the report-only close. */}
          {(canCancel(event) || footer) && (
            <div className="flex justify-end gap-2">
              {/* Cancelar is as state-changing as Aprobar: neither is offered while an execute's
                  outcome is unknown. */}
              {canCancel(event) && !executeOutcomeUnknown && (
                <ButtonLoader
                  type="secondary"
                  onClick={() =>
                    run(cancelCoverageEvent, {
                      key: 'cancel',
                      successKey: 'canceled',
                      confirmKey: 'confirm_cancel',
                      noticeParams: result => ({ count: result?.deleted_children ?? 0 }),
                      // Once the event has left `open`, cancelling settles the frozen proposal
                      // away and the platform asks for settler authority; a 401 then names it.
                      action: showsReconciliation ? 'cancel' : undefined,
                    })
                  }
                  loading={pending === 'cancel'}
                  disabled={busy}
                  loading_text={i18n.t(`${EVENTS_T}.reconciliation.loading`)}
                >
                  {i18n.t(`${T}.cancel`)}
                </ButtonLoader>
              )}
              {footer === 'retry_reconcile' && (
                <ButtonLoader
                  type="secondary"
                  onClick={() => runReconcile('confirm_retry_reconcile')}
                  loading={pending === 'reconcile'}
                  disabled={busy}
                  loading_text={i18n.t(`${EVENTS_T}.reconciliation.loading`)}
                >
                  {i18n.t(`${EVENTS_T}.reconciliation.retry_reconcile`)}
                </ButtonLoader>
              )}
              {footer === 'reconcile' && (
                <ButtonLoader
                  type="primary"
                  onClick={() => runReconcile()}
                  loading={pending === 'reconcile'}
                  disabled={busy}
                  loading_text={i18n.t(`${EVENTS_T}.reconciliation.loading`)}
                >
                  {i18n.t(`${EVENTS_T}.reconciliation.reconcile`)}
                </ButtonLoader>
              )}
              {footer === 'close_legacy' && (
                <ButtonLoader
                  type="primary"
                  onClick={() => run(closeCoverageEvent, { key: 'close', successKey: 'closed', confirmKey: 'confirm_close' })}
                  loading={pending === 'close'}
                  disabled={busy}
                  loading_text={i18n.t(`${EVENTS_T}.reconciliation.loading`)}
                >
                  {i18n.t(`${T}.close`)}
                </ButtonLoader>
              )}
            </div>
          )}
        </div>
      )}
    </SlidePanel>
  )
}
